import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Drawer, Box, Typography, IconButton, Divider, Paper, Avatar, Stack, Menu, MenuItem, ListItemIcon, Dialog, DialogActions, DialogContent, DialogTitle, TextField, DialogContentText, Button, Tabs, Tab, Switch, FormControlLabel, Slider, Snackbar, Alert, Grid, Tooltip, useMediaQuery, useTheme
} from '@mui/material';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import 'leaflet-geosearch/dist/geosearch.css';
import L from 'leaflet';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PublicIcon from '@mui/icons-material/Public';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import workplaceService from '../services/workplace.service';
import WorkplaceQREditor from './workplace-qr/WorkplaceQREditor';

// New component for Geofencing Controls
const GeofencingControls = ({ geofencingEnabled, setGeofencingEnabled, geofencingRadius, setGeofencingRadius }) => {
    const theme = useTheme();
    return (
        <Stack spacing={3}>
            <Box>
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                    <PublicIcon color="action" />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>Activation</Typography>
                </Stack>
                <FormControlLabel
                    control={<Switch checked={geofencingEnabled} onChange={(e) => setGeofencingEnabled(e.target.checked)} sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#2563EB', '&:hover': { backgroundColor: 'rgba(37, 99, 235, 0.08)' } },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2563EB' },
                    }} />}
                    label={<Typography sx={{ color: 'text.primary' }}>Enable Geofencing</Typography>}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1, ml: '48px' }}>
                    Requires employees to be at this location to start a shift.
                </Typography>
            </Box>
            <Divider />
            <Box sx={{ opacity: geofencingEnabled ? 1 : 0.5, transition: 'opacity 0.3s' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                    <SettingsEthernetIcon color="action" />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>Radius</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Set the allowed perimeter from the central pin.
                </Typography>
                <Slider
                    value={geofencingRadius}
                    onChange={(e, newValue) => setGeofencingRadius(newValue)}
                    aria-labelledby="geofencing-radius-slider"
                    valueLabelFormat={(value) => `${value}m`}
                    valueLabelDisplay="auto"
                    step={10}
                    min={10}
                    max={1000}
                    disabled={!geofencingEnabled}
                    sx={{ 
                        color: '#2563EB',
                        '& .MuiSlider-valueLabel': {
                            backgroundColor: theme.palette.mode === 'dark' ? '#2563EB' : '#2563EB',
                            color: 'white',
                        },
                    }}
                />
            </Box>
        </Stack>
    );
};


// Fix for default Leaflet icon issue with Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const greenIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const getInitials = (name) => {
    if (!name) return '';
    const nameParts = name.trim().split(' ');
    if (nameParts.length > 1) {
        return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
    } else if (nameParts[0] && nameParts[0].length > 1) {
        return nameParts[0].substring(0, 2).toUpperCase();
    }
    return (name[0] || '').toUpperCase();
};

const modalTextFieldStyle = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#2563EB',
        },
    },
    '& label.Mui-focused': {
        color: '#2563EB',
    },
};

const WorkplaceDetailDrawer = ({ workplace, open, onClose, onWorkplaceDeleted, onWorkplaceUpdated }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [editingWorkplaceName, setEditingWorkplaceName] = useState('');
    const [currentTab, setCurrentTab] = useState('qr');
    const [geofencingEnabled, setGeofencingEnabled] = useState(false);
    const [geofencingRadius, setGeofencingRadius] = useState(100);
    const [markerPosition, setMarkerPosition] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [isMapDialogOpen, setMapDialogOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


    useEffect(() => {
        if (workplace) {
            setEditingWorkplaceName(workplace.name);
            setGeofencingEnabled(workplace.geofencing_enabled || false);
            setGeofencingRadius(workplace.geofencing_radius || 100);
            if (workplace.latitude && workplace.longitude) {
                setMarkerPosition({ lat: workplace.latitude, lng: workplace.longitude });
            } else {
                setMarkerPosition({ lat: 48.148598, lng: 17.107748 });
            }
        }
    }, [workplace]);

    useEffect(() => {
        if (!open) {
            setCurrentTab('qr');
        }
    }, [open]);

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const qrCodeUrl = workplace ? `${process.env.REACT_APP_QR_BASE_URL || 'https://effzone.com'}/mobile-app/${workplace.qr_code_id}` : '';

    const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const handleOpenEditModal = () => {
        setEditModalOpen(true);
        handleMenuClose();
    };

    const handleCloseEditModal = () => setEditModalOpen(false);

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    const handleEditSubmit = async () => {
        try {
            // Send all workplace data, not just the name
            await workplaceService.updateWorkplace(workplace.id, { ...workplace, name: editingWorkplaceName });
            if (onWorkplaceUpdated) {
                onWorkplaceUpdated();
            }
            handleCloseEditModal();
            setSnackbar({ open: true, message: 'Workplace name updated successfully!', severity: 'success' });
        } catch (error) {
            console.error("Failed to update workplace", error);
            setSnackbar({ open: true, message: 'Failed to update workplace name.', severity: 'error' });
        }
    };

    const handleGeofencingSave = async () => {
        try {
            const dataToUpdate = {
                ...workplace,
                name: workplace.name,
                geofencing_enabled: geofencingEnabled,
                geofencing_radius: geofencingEnabled ? geofencingRadius : null,
                latitude: markerPosition ? markerPosition.lat : null,
                longitude: markerPosition ? markerPosition.lng : null,
            };

            await workplaceService.updateWorkplace(workplace.id, dataToUpdate);
            setSnackbar({ open: true, message: 'Geofencing settings saved!', severity: 'success' });
            if (onWorkplaceUpdated) {
                onWorkplaceUpdated();
            }
        } catch (error) {
            console.error("Failed to update geofencing settings", error);
            setSnackbar({ open: true, message: 'Failed to save geofencing settings.', severity: 'error' });
        }
    };

    const handleDelete = () => {
        if (onWorkplaceDeleted) {
            onWorkplaceDeleted(workplace.id);
        }
        setDeleteDialogOpen(false);
    };


    if (!workplace) {
        return null;
    }

    return (
        <>
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                keepMounted={false}
                disableRestoreFocus={true}
            >
                <Box sx={{ width: { xs: '100vw', sm: 400 }, p: 3 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
                            <IconButton onClick={onClose}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Avatar sx={{ bgcolor: '#2563EB', color: '#ffffff', width: 50, height: 50, fontSize: '1.2rem' }}>
                                {getInitials(workplace?.name || '')}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {workplace?.name}
                                </Typography>
                            </Box>
                        </Stack>
                        <IconButton
                            aria-label="more"
                            onClick={handleMenuClick}
                            sx={{ color: 'text.secondary' }}
                        >
                            <MoreVertIcon />
                        </IconButton>
                    </Stack>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                            value={currentTab}
                            onChange={handleTabChange}
                            variant="fullWidth"
                            aria-label="workplace details tabs"
                            sx={{
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#2563EB',
                                },
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    '&.Mui-selected': {
                                        color: '#2563EB',
                                    },
                                },
                            }}
                        >
                            <Tab label="QR Code" value="qr" />
                            <Tab label="Geofencing" value="geo" />
                        </Tabs>
                    </Box>

                    {currentTab === 'qr' && (
                        <Box>
                            <WorkplaceQREditor
                                qrCodeUrl={qrCodeUrl}
                                workplace={workplace}
                                onWorkplaceUpdated={onWorkplaceUpdated}
                            />
                        </Box>
                    )}

                    {currentTab === 'geo' && (
                        <Box sx={{ mt: 3, p: 2, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                            <Typography variant="body1" sx={{ mb: 2, color: 'text.primary' }}>
                                Geofencing is currently: <Box component="span" sx={{ fontWeight: 'bold', color: geofencingEnabled ? 'success.main' : 'error.main' }}>{geofencingEnabled ? 'Enabled' : 'Disabled'}</Box>
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => setMapDialogOpen(true)}
                                sx={{ bgcolor: '#2563EB', '&:hover': { bgcolor: '#1D4ED8' } }}
                            >
                                Configure Geofencing
                            </Button>
                        </Box>
                    )}

                </Box>
            </Drawer>

            <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleMenuClose}
                keepMounted={false}
                disableRestoreFocus={true}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5,
                        borderRadius: '16px',
                        p: 1,
                        '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 1,
                        },
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                }}
            >
                <MenuItem onClick={handleOpenEditModal}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    Edit Workplace
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { setDeleteDialogOpen(true); handleMenuClose(); }} sx={{ color: 'error.main' }}>
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    Delete Workplace
                </MenuItem>
            </Menu>

            <Dialog
                open={isEditModalOpen}
                onClose={handleCloseEditModal}
                fullWidth
                maxWidth="sm"
                keepMounted={false}
                disableRestoreFocus={true}
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>Edit Workplace Name</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Workplace Name"
                        type="text"
                        fullWidth
                        value={editingWorkplaceName}
                        onChange={(e) => setEditingWorkplaceName(e.target.value)}
                        sx={modalTextFieldStyle}
                    />
                </DialogContent>
                <DialogActions sx={{ p: '16px 24px' }}>
                    <Button onClick={handleCloseEditModal} sx={{ color: 'text.secondary', borderRadius: '12px' }}>Cancel</Button>
                    <Button onClick={handleEditSubmit} variant="contained" sx={{ bgcolor: '#2563EB', color: 'white', borderRadius: '12px', boxShadow: 'none', '&:hover': { bgcolor: '#1D4ED8', boxShadow: 'none' } }}>Save Changes</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isDeleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                keepMounted={false}
                disableRestoreFocus={true}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle id="alert-dialog-title" sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                    {"Are you sure you want to delete this workplace?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        This action is permanent and cannot be undone. All data associated with this workplace will be deleted.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: '16px 24px' }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'text.secondary', borderRadius: '12px' }}>Cancel</Button>
                    <Button onClick={handleDelete} autoFocus color="error" variant="contained" sx={{ borderRadius: '12px', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isMapDialogOpen}
                onClose={() => setMapDialogOpen(false)}
                fullScreen={isMobile}
                fullWidth
                maxWidth="lg"
                PaperProps={{
                    sx: {
                        height: isMobile ? '100%' : '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: isMobile ? 0 : 4
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                        Geofencing Configuration
                    </Typography>
                    <IconButton onClick={() => setMapDialogOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                {isMobile ? (
                    <>
                        <DialogContent sx={{ p: 0, flexGrow: 1, position: 'relative' }}>
                            <MapComponent
                                markerPosition={markerPosition}
                                setMarkerPosition={setMarkerPosition}
                                geofencingEnabled={geofencingEnabled}
                                geofencingRadius={geofencingRadius}
                            />
                        </DialogContent>
                        <DialogActions sx={{ p: 3, flexDirection: 'column', borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                            <GeofencingControls
                                geofencingEnabled={geofencingEnabled}
                                setGeofencingEnabled={setGeofencingEnabled}
                                geofencingRadius={geofencingRadius}
                                setGeofencingRadius={setGeofencingRadius}
                            />
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => { handleGeofencingSave(); setMapDialogOpen(false); }}
                                sx={{ mt: 2, bgcolor: '#2563EB', color: 'white', '&:hover': { bgcolor: '#1D4ED8' } }}
                            >
                                Save Configuration
                            </Button>
                        </DialogActions>
                    </>
                ) : (
                    <>
                        <DialogContent sx={{ p: 0, position: 'relative', overflow: 'hidden', flexGrow: 1 }}>
                            <MapComponent
                                markerPosition={markerPosition}
                                setMarkerPosition={setMarkerPosition}
                                geofencingEnabled={geofencingEnabled}
                                geofencingRadius={geofencingRadius}
                            />
                            <Paper
                                elevation={4}
                                sx={{
                                    position: 'absolute', top: 16, left: 16, zIndex: 1000,
                                    p: 3, width: 360,
                                    borderRadius: 3, 
                                    bgcolor: (theme) => theme.palette.mode === 'dark' 
                                        ? 'rgba(30, 30, 30, 0.95)' 
                                        : 'rgba(255, 255, 255, 0.95)',
                                    backdropFilter: 'blur(8px)',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                                    overflowY: 'auto'
                                }}
                            >
                                <GeofencingControls
                                    geofencingEnabled={geofencingEnabled}
                                    setGeofencingEnabled={setGeofencingEnabled}
                                    geofencingRadius={geofencingRadius}
                                    setGeofencingRadius={setGeofencingRadius}
                                />
                            </Paper>
                        </DialogContent>
                        <DialogActions sx={{ p: '16px 24px', borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                            <Button onClick={() => setMapDialogOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={() => { handleGeofencingSave(); setMapDialogOpen(false); }}
                                sx={{ bgcolor: '#2563EB', color: 'white', '&:hover': { bgcolor: '#1D4ED8' } }}
                            >
                                Save and Close
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </>
    );
};

const MapComponent = ({ markerPosition, setMarkerPosition, geofencingEnabled, geofencingRadius }) => {
    return (
        <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
            {markerPosition && (
                <MapContainer center={markerPosition} zoom={15} style={{ height: '100%', width: '100%' }} zoomControl={false}>
                    <TileLayer
                        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                    />
                    <ZoomControl position="bottomleft" />
                    <SearchField setPosition={setMarkerPosition} />
                    <DraggableMarker position={markerPosition} setPosition={setMarkerPosition} />
                    {geofencingEnabled && <Circle center={markerPosition} radius={geofencingRadius} pathOptions={{ color: '#2563EB', fillColor: '#2563EB' }} />}
                    <RecenterAutomatically position={markerPosition} />
                </MapContainer>
            )}
            <Tooltip title="Use My Current Location">
                <IconButton
                    onClick={() => {
                        navigator.geolocation.getCurrentPosition(position => {
                            const { latitude, longitude } = position.coords;
                            setMarkerPosition({ lat: latitude, lng: longitude });
                        });
                    }}
                    sx={{
                        position: 'absolute', top: 16, right: 16, zIndex: 1000,
                        bgcolor: '#2563EB', color: 'white', boxShadow: 3,
                        '&:hover': { bgcolor: '#1D4ED8' }
                    }}
                >
                    <MyLocationIcon />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

function DraggableMarker({ position, setPosition }) {
    const markerRef = useRef(null);
    const map = useMapEvents({
        click(e) {
            setPosition(e.latlng);
            map.flyTo(e.latlng, map.getZoom());
        },
    });

    const eventHandlers = useMemo(
        () => ({
            dragend() {
                const marker = markerRef.current;
                if (marker != null) {
                    setPosition(marker.getLatLng());
                }
            },
        }),
        [],
    );

    return (
        <Marker
            draggable={true}
            eventHandlers={eventHandlers}
            position={position}
            ref={markerRef}
            icon={greenIcon}>
        </Marker>
    );
}

const RecenterAutomatically = ({ position }) => {
    const map = useMap();
    useEffect(() => {
        if (position) {
            map.flyTo([position.lat, position.lng], map.getZoom());
        }
    }, [position]);
    return null;
};

const SearchField = ({ setPosition }) => {
    const map = useMap();
    const theme = useTheme();
    useEffect(() => {
        const provider = new OpenStreetMapProvider();
        const searchControl = new GeoSearchControl({
            provider,
            style: 'bar',
            showMarker: false,
            showPopup: false,
            autoClose: true,
            retainZoomLevel: false,
            animateZoom: true,
            keepResult: true,
            searchLabel: 'Search for an address...',
            classNames: {
                container: 'geosearch-custom-container',
                form: 'geosearch-custom-form',
                input: 'geosearch-custom-input',
            }
        });
        map.addControl(searchControl);
        map.on('geosearch/showlocation', (result) => {
            const { x, y } = result.location;
            setPosition({ lat: y, lng: x });
        });
        return () => map.removeControl(searchControl);
    }, [map, setPosition]);

    const inputBgColor = theme.palette.mode === 'dark' 
        ? theme.palette.background.paper 
        : 'white';
    const inputTextColor = theme.palette.mode === 'dark'
        ? theme.palette.text.primary
        : theme.palette.text.primary;

    return (
        <style>
            {`
                .geosearch-custom-container {
                    position: absolute;
                    top: 20px;
                    left: 50%;
                    transform: translateX(-50%);
                    z-index: 1000;
                    width: clamp(300px, 50%, 600px);
                }
                .geosearch-custom-form {
                    display: flex;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.2) !important;
                    border-radius: 12px !important;
                    overflow: hidden;
                }
                .geosearch-custom-input {
                    height: 52px;
                    font-size: 1rem;
                    padding: 0 16px;
                    border: 2px solid transparent !important;
                    background-color: ${inputBgColor} !important;
                    color: ${inputTextColor} !important;
                }
                .geosearch-custom-input:focus {
                    border-color: #2563EB !important;
                }
                .geosearch-custom-input::placeholder {
                    color: ${theme.palette.text.secondary} !important;
                }
            `}
        </style>
    );
};

export default WorkplaceDetailDrawer; 